/**
 * The language codes Meta accepts for a WhatsApp message template, and the
 * rule that derives one from a country's BCP-47 languageTag.
 *
 * Twin of `functions/aiConversation/templateLanguage.js` in
 * lead1-crm-cloud-functions: same list, same rule, kept in step by hand. The
 * form uses it for the default of an agent's template language, the engine as
 * the fallback when the agent has none: if the two disagreed, the language
 * the form shows would not be the one the engine sends.
 *
 * Meta's codes are not BCP-47 tags: `it-IT` is `it`, `de-DE` is `de`, but
 * `pt-BR` is `pt_BR` and `en-GB` is `en_GB`.
 */
const CODES = [
  'af',
  'sq',
  'ar',
  'az',
  'bn',
  'bg',
  'ca',
  'zh_CN',
  'zh_HK',
  'zh_TW',
  'hr',
  'cs',
  'da',
  'nl',
  'en',
  'en_GB',
  'en_US',
  'et',
  'fil',
  'fi',
  'fr',
  'ka',
  'de',
  'el',
  'gu',
  'ha',
  'he',
  'hi',
  'hu',
  'id',
  'ga',
  'it',
  'ja',
  'kn',
  'kk',
  'rw_RW',
  'ko',
  'ky_KG',
  'lo',
  'lv',
  'lt',
  'mk',
  'ms',
  'ml',
  'mr',
  'nb',
  'fa',
  'pl',
  'pt_BR',
  'pt_PT',
  'pa',
  'ro',
  'ru',
  'sr',
  'sk',
  'sl',
  'es',
  'es_AR',
  'es_ES',
  'es_MX',
  'sw',
  'sv',
  'ta',
  'te',
  'th',
  'tr',
  'uk',
  'ur',
  'uz',
  'vi',
  'zu',
];

/** As select options: the code is what Meta wants, so it is also the label. */
export const WHATSAPP_TEMPLATE_LANGUAGES = CODES.map((code) => ({
  value: code,
  label: code,
}));

/**
 * The Meta code for a languageTag, or null when there is no sensible one.
 *
 * The region wins when Meta has it (`pt-BR` → `pt_BR`), then the language
 * alone (`de-DE` → `de`), then the first regional variant of the language
 * Meta knows (`rw` → `rw_RW`). Norwegian is the one rename: Meta files it as
 * Bokmål, `nb`, while a tag may say `no`.
 */
export const metaLanguageFromTag = (languageTag) => {
  if (!languageTag) return null;

  const [language = '', region = ''] = String(languageTag).split(/[-_]/);
  const lang = language.toLowerCase();
  const regionCode = region.toUpperCase();
  if (!lang) return null;

  if (regionCode && CODES.includes(`${lang}_${regionCode}`))
    return `${lang}_${regionCode}`;
  if (lang === 'no') return 'nb';
  if (CODES.includes(lang)) return lang;
  return CODES.find((code) => code.startsWith(`${lang}_`)) || null;
};

export default WHATSAPP_TEMPLATE_LANGUAGES;
